import React, { useState, useEffect } from 'react';
import { Search, X, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import FirmaCanvas from './FirmaCanvas';
import '../../styles/AttestazioniTab.css';

/**
 * ModuloAttestazione — compilazione e firma dell'attestazione di
 * finalita' terapeutica, in finestra modale.
 *
 * Si usa in due punti: dal tab dedicato, e come passo intermedio del
 * salvataggio di una procedura, quando il medico ha chiesto una nuova
 * attestazione per una prestazione esentabile.
 *
 * Props:
 *  - pazienteId
 *  - trattamentoIniziale   nome della prestazione, precompila il campo
 *  - onCompletata          (attestazioneId) => void, a firma avvenuta
 *  - onAnnulla             () => void
 */

const FINALITA = [
    ['fin_diagnosticare', 'diagnosticare un problema di salute'],
    ['fin_curare', 'curare una patologia o un problema di salute'],
    ['fin_tutelare', 'tutelare la salute del paziente'],
    ['fin_mantenere', 'mantenere la salute del paziente'],
    ['fin_ristabilire', 'ristabilire la salute del paziente'],
    ['fin_benessere', 'intervenire sul benessere psico-fisico del paziente'],
];

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

const ModuloAttestazione = ({ pazienteId, trattamentoIniziale, onCompletata, onAnnulla }) => {
    const [form, setForm] = useState({
        quadro_clinico: '', compromissione: '',
        trattamento: trattamentoIniziale || '', distretto_corporeo: '',
        numero_sedute: '', motivazione_clinica: '',
        data_prestazione: new Date().toISOString().slice(0, 10),
        valida_al: '',
        fin_diagnosticare: false, fin_curare: false, fin_tutelare: false,
        fin_mantenere: false, fin_ristabilire: false, fin_benessere: false,
    });
    const [ricerca, setRicerca] = useState('');
    const [codici, setCodici] = useState([]);
    const [icd, setIcd] = useState(null);
    const [passo, setPasso] = useState('dati');    // dati | firma
    const [firma, setFirma] = useState(null);
    const [attestazioneId, setAttestazioneId] = useState(null);
    const [errore, setErrore] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    /* Ricerca ICD-10 fra i 12.042 codici, con attesa fra un tasto e l'altro */
    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                const r = await api.get('/attestazioni/icd10', { params: { q: ricerca } });
                setCodici(r.data.data.codici || []);
            } catch (e) {
                console.error('Ricerca ICD-10:', e);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [ricerca]);

    const almenoUna = FINALITA.some(([k]) => form[k]);
    /* Il codice ICD-10 e' consigliato, non obbligatorio: l'esenzione la
       produce l'attestazione firmata, il codice serve alle statistiche
       (confermato dal commercialista il 21 settembre 2026). */
    const completo = form.quadro_clinico && form.trattamento
        && form.motivazione_clinica && form.data_prestazione && almenoUna;

    const salva = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const r = await api.post('/attestazioni', {
                ...form,
                paziente_id: pazienteId,
                icd10_codice: icd ? icd.codice : null,
                numero_sedute: form.numero_sedute ? parseInt(form.numero_sedute, 10) : null,
                compromissione: form.compromissione || null,
                distretto_corporeo: form.distretto_corporeo || null,
                valida_al: form.valida_al || null,
            });
            setAttestazioneId(r.data.data.attestazione.id);
            setPasso('firma');
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    const conferma = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            await api.post(`/attestazioni/${attestazioneId}/firma`, { firma_immagine: firma });
            onCompletata(attestazioneId);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella firma'));
        } finally {
            setInCorso(false);
        }
    };

    return (
        <div className="att-modale-sfondo">
            <div className="att-modale">
                <div className="att-card-head">
                    <span>
                        {passo === 'dati'
                            ? 'Attestazione di finalità terapeutica'
                            : 'Firma dell\u2019attestazione'}
                    </span>
                    <button className="att-chiudi" onClick={onAnnulla}><X size={16} /></button>
                </div>

                <div className="att-modale-corpo">
                    {errore && (
                        <div className="att-errore">
                            <AlertCircle size={16} /> {errore}
                        </div>
                    )}

                    {passo === 'dati' ? (
                        <>
                            <p className="att-nota">
                                Senza questa attestazione firmata la prestazione resta imponibile.
                            </p>

                            <div className="att-sezione">Valutazione clinica</div>

                            <label className="att-campo">
                                <span>Diagnosi ICD-10 <small>(se lo conosci: aiuta le ricerche e le statistiche, non è richiesto per l'esenzione)</small></span>
                                {icd ? (
                                    <div className="att-icd-scelto">
                                        <b>{icd.codice}</b> {icd.titolo}
                                        <button onClick={() => { setIcd(null); setRicerca(''); }}>
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="att-ricerca">
                                            <Search size={16} />
                                            <input
                                                autoFocus
                                                placeholder="Cerca fra i codici: scrivi codice o descrizione…"
                                                value={ricerca}
                                                onChange={(e) => setRicerca(e.target.value)}
                                            />
                                        </div>
                                        {codici.length > 0 && (
                                            <div className="att-risultati">
                                                {codici.map((c) => (
                                                    <button key={c.codice} type="button"
                                                            className="att-risultato"
                                                            onClick={() => setIcd(c)}>
                                                        <b>{c.codice}</b> {c.titolo}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {!ricerca && (
                                            <p className="att-nota">
                                                Senza ricerca vengono proposti i codici usati più spesso.
                                            </p>
                                        )}
                                    </>
                                )}
                            </label>

                            <label className="att-campo">
                                <span>Quadro clinico e sintomatologia rilevata *</span>
                                <textarea rows={3} value={form.quadro_clinico}
                                    onChange={(e) => setForm({ ...form, quadro_clinico: e.target.value })} />
                            </label>

                            <label className="att-campo">
                                <span>Compromissione funzionale o del benessere psico-fisico</span>
                                <textarea rows={2} value={form.compromissione}
                                    onChange={(e) => setForm({ ...form, compromissione: e.target.value })} />
                            </label>

                            <div className="att-sezione">Prestazione indicata</div>

                            <label className="att-campo">
                                <span>Trattamento raccomandato *</span>
                                <textarea rows={2} value={form.trattamento}
                                    onChange={(e) => setForm({ ...form, trattamento: e.target.value })} />
                            </label>

                            <div className="att-riga">
                                <label className="att-campo">
                                    <span>Distretto corporeo</span>
                                    <input value={form.distretto_corporeo}
                                        onChange={(e) => setForm({ ...form, distretto_corporeo: e.target.value })} />
                                </label>
                                <label className="att-campo att-campo--stretto">
                                    <span>N. sedute</span>
                                    <input type="number" min="1" value={form.numero_sedute}
                                        onChange={(e) => setForm({ ...form, numero_sedute: e.target.value })} />
                                </label>
                            </div>

                            <div className="att-riga">
                                <label className="att-campo">
                                    <span>Data della prestazione *</span>
                                    <input type="date" value={form.data_prestazione}
                                        onChange={(e) => setForm({ ...form, data_prestazione: e.target.value })} />
                                </label>
                                <label className="att-campo">
                                    <span>Valida fino al</span>
                                    <input type="date" value={form.valida_al}
                                        onChange={(e) => setForm({ ...form, valida_al: e.target.value })} />
                                </label>
                            </div>
                            <p className="att-nota">
                                Indicando una data di validità l'attestazione copre tutto il ciclo:
                                le sedute successive la riusano senza rifarla.
                            </p>

                            <div className="att-sezione">Finalità attestate *</div>
                            <div className="att-finalita">
                                {FINALITA.map(([chiave, testo]) => (
                                    <label key={chiave} className="att-check">
                                        <input type="checkbox" checked={!!form[chiave]}
                                            onChange={(e) => setForm({ ...form, [chiave]: e.target.checked })} />
                                        {testo}
                                    </label>
                                ))}
                            </div>

                            <label className="att-campo">
                                <span>Motivazione clinica *</span>
                                <textarea rows={3} value={form.motivazione_clinica}
                                    onChange={(e) => setForm({ ...form, motivazione_clinica: e.target.value })} />
                            </label>

                            <div className="att-azioni">
                                <button type="button" className="att-btn" onClick={onAnnulla}>
                                    Annulla
                                </button>
                                <button type="button" className="att-btn att-btn--principale"
                                        onClick={salva} disabled={!completo || inCorso}>
                                    Avanti, firma
                                </button>
                            </div>
                            {!completo && (
                                <p className="att-nota">
                                    Servono diagnosi, quadro clinico, trattamento, motivazione, data
                                    e almeno una finalità.
                                </p>
                            )}
                        </>
                    ) : (
                        <>
                            <p className="att-nota">
                                Firmando, l'attestazione viene archiviata nel fascicolo del paziente
                                e la prestazione diventa esente.
                            </p>
                            <FirmaCanvas valore={firma} onChange={setFirma}
                                         etichetta="Firma del medico" />
                            <div className="att-azioni">
                                <button type="button" className="att-btn" onClick={onAnnulla}>
                                    Annulla
                                </button>
                                <button type="button" className="att-btn att-btn--principale"
                                        onClick={conferma} disabled={!firma || inCorso}>
                                    Firma
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModuloAttestazione;
